<template>
  <div ref="wrap" class="mapscene" :class="{ 'mapscene--ready': ready }">
    <canvas
      ref="canvas"
      class="mapscene__canvas"
      :class="{ 'mapscene__canvas--grabbing': dragging }"
      @pointerdown="onDown"
      @pointermove="onPointerMove"
      @pointerup="onUp"
      @pointercancel="onCancel"
      @wheel.prevent="onWheel"
      @contextmenu.prevent
    />
    <!--
      Crisp HTML over the 3D, not text baked into the scene. The band says
      who it is on the map itself - Matt: "I see a number one moving around
      on the map. Is that the band of one?" - and every camp carries the
      strength only the Frontier gets to see.
    -->
    <div class="mapscene__labels">
      <span
        v-for="l in placed"
        :key="l.id"
        class="maplabel"
        :class="`maplabel--${l.kind}`"
        :style="{ left: `${l.x}px`, top: `${l.y}px`, ...(l.colour ? { '--people': l.colour } : {}) }"
        :title="l.title"
      >{{ l.text }}</span>
    </div>
    <div v-if="showYields" class="mapscene__labels" aria-hidden="true">
      <!-- An icon, not a word: thirty "food"s on top of each other was the
           old overlay. A gold ring where a village could be planted. -->
      <span
        v-for="y in yieldsPlaced"
        :key="y.id"
        class="mapyield"
        :class="{ 'mapyield--found': y.foundable }"
        :style="{ left: `${y.x}px`, top: `${y.y}px`, '--res': resColour(y.makes) }"
        :title="y.foundable ? `A village here would make ${y.makes}` : y.makes"
      >
        <!-- eslint-disable-next-line vue/no-v-html -- constant art from resourceArt.js -->
        <svg viewBox="0 0 16 16" v-html="resArt(y.makes)" />
      </span>
    </div>
    <span v-if="burstAt" :key="burst.key" class="mapburst" :style="{ left: `${burstAt.x}px`, top: `${burstAt.y}px` }" aria-hidden="true" />
    <button v-if="offBand" class="mapscene__find" @click="findBand">Find them</button>
    <button v-if="moved" class="mapscene__reset" @click="reset">Reset view</button>
    <p v-if="!ready" class="mapscene__loading">Drawing the island…</p>
  </div>
</template>

<script>
// The Frontier's island as the table sees it: the same Kenney models, a
// fixed isometric camera you can swing and pull in. three.js loads only when
// this board is on screen, and the phone never downloads it for any other
// role.
// How far a finger may wander before a press is a drag (and a hold is off).
// 8px was the fleet's tap threshold; a hold lasts half a second and a real
// thumb drifts more than that in the time, so the hold gets some slack.
import { RESOURCES, artForResource } from './resourceArt';
import { loadLazy } from '@/utils/preloadRecovery';

const DRAG_PX = 12;

export default {
  name: 'FrontierScene',
  props: {
    map: { type: Object, required: true },
    // The unit in hand, so the camera can be sent back to it.
    at: { type: Object, default: () => null },
    // { id, kind, text, ref } - hung over the thing with that id.
    labels: { type: Array, default: () => [] },
    // A fight just happened here: { q, r, key } (FrontierBoard clears it).
    burst: { type: Object, default: null },
    // Label every seen hex with what a day's work there makes (playtest 11).
    showYields: { type: Boolean, default: false },
  },
  emits: ['pick', 'inspect'],
  data() {
    return {
      burstAt: null,
      ready: false, placed: [], yieldsPlaced: [], dragging: false, moved: false, offBand: false,
    };
  },
  watch: {
    // Toggling the overlay draws it now, not at the next sync.
    showYields() { this.$nextTick(() => this.place()); },
    map: {
      handler() { this.draw(); },
      deep: true,
    },
    labels() { this.place(); },
    burst() { this.place(); },
  },
  created() {
    this.pointers = new Map();
    this.down = null;
    this.pinch = 0;
    this.hold = null;
    this.held = false;
  },
  async mounted() {
    const loaded = await loadLazy(
      () => import('@/world/frontierWorld'),
      () => import('@/utils/eventTheatre'),
    );
    // null: a lazy file failed and the page is already reloading.
    if (!loaded || !this.$refs.canvas) return;
    const [{ createFrontierWorld }, { prefersReducedMotion }] = loaded;
    this.world = createFrontierWorld(this.$refs.canvas, { reducedMotion: prefersReducedMotion() });
    this.world.setOnMove(() => this.place());
    if (import.meta.env.DEV) window.__island = this.world;
    this.observer = new ResizeObserver(() => this.fit());
    this.observer.observe(this.$refs.wrap);
    this.fit();
    await this.world.ready;
    this.ready = true;
    this.draw();
  },
  beforeUnmount() {
    this.clearHold();
    if (this.observer) this.observer.disconnect();
    if (this.world) this.world.dispose();
  },
  methods: {
    resArt(kind) { return artForResource(kind); },
    resColour(kind) { return RESOURCES[kind]?.colour || '#fff'; },
    isOff(x, y) {
      const el = this.$refs.wrap;
      if (!el) return false;
      return x < 24 || y < 24 || x > el.clientWidth - 24 || y > el.clientHeight - 24;
    },
    /**
     * Centre on a hex. This is for an EXPLICIT request - tapping a unit in
     * the list - where going to look at it is the whole point.
     */
    lookAt(q, r) {
      if (this.world) { this.world.lookAtHex(q, r); this.moved = true; }
    },
    /** Tap the compass: north straight up, or back to the table's angle (playtest 11). */
    toggleNorth() {
      if (!this.world) return false;
      const up = this.world.toggleNorth();
      this.$emit('north', up);
      return up;
    },
    /**
     * Pan to a hex ONLY if it is off screen. This is for auto-advance, and
     * the difference matters: Matt, 2026-09-19, "every time I do something
     * it seems to reset to center - I don't want that. The map shouldn't
     * move when I do that."
     */
    bringIntoView(q, r) {
      if (!this.world) return;
      const at = this.world.screenOfHex ? this.world.screenOfHex(q, r) : null;
      if (at && !this.isOff(at.x, at.y)) return;
      this.world.lookAtHex(q, r);
      this.moved = true;
    },
    fit() {
      const el = this.$refs.wrap;
      if (!el || !this.world) return;
      this.world.resize(el.clientWidth, el.clientHeight);
      this.place();
    },
    draw() {
      if (!this.world || !this.ready) return;
      this.world.sync(this.map);
      this.$nextTick(() => this.place());
    },
    place() {
      if (!this.world || !this.ready) return;
      this.burstAt = this.burst && this.world.screenOfHex ? this.world.screenOfHex(this.burst.q, this.burst.r) : null;
      this.yieldsPlaced = !this.showYields || !this.world.screenOfHex ? [] : (this.map.tiles || [])
        .filter((t) => t.makes)
        .map((t) => ({ id: t.id, makes: t.makes, foundable: t.foundable, ...(this.world.screenOfHex(t.q, t.r, 0.3) || {}) }))
        .filter((t) => Number.isFinite(t.x));
      this.placed = this.labels
        .map((l) => {
          const at = this.world.screenOf(l.id);
          if (!at) return null;
          // Kept inside the card: a people's name at the edge was cut off
          // ("The Tall Kings (friend"). Centred labels, so a margin each side.
          const w = this.$refs.wrap?.clientWidth || 0;
          const m = l.kind === 'rival' ? 92 : 50;
          const x = w > 2 * m ? Math.min(Math.max(at.x, m), w - m) : at.x;
          return { ...l, ...at, x };
        })
        .filter((l) => l && Number.isFinite(l.x));
          // Once you have pushed the island about, the unit in hand can end up
      // off the side of it. One button brings it back rather than a Reset
      // that also undoes your angle.
      const mine = this.placed.find((l) => l.kind === 'band');
      const el = this.$refs.wrap;
      this.offBand = Boolean(mine && el) && this.isOff(mine.x, mine.y);
    },
    // A drag is never a tap (8px), the way the city and the table behave.
    onDown(e) {
      if (!this.world) return;
      this.$refs.canvas.setPointerCapture?.(e.pointerId);
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pointers.size === 1) {
        this.down = { x: e.clientX, y: e.clientY, moved: 0 };
        // Press and hold any hex to ask what it is. The same 500ms the rest
        // of the fleet uses for a long press on the build stamp.
        this.hold = window.setTimeout(() => { this.holdFired(); }, 500);
      }
      // A second finger means a pinch, and a pinch must never end in a tap:
      // its moves are consumed by the zoom, so the one-finger drag counter
      // stays at zero and the release looked like a tap on the ground under
      // it. Matt: "the scout was moving as I touched things."
      if (this.pointers.size > 1) { this.down = null; this.clearHold(); this.pinch = this.spread(); }
    },
    onPointerMove(e) {
      if (!this.world || !this.pointers.has(e.pointerId)) return;
      const last = this.pointers.get(e.pointerId);
      const dx = e.clientX - last.x;
      const dy = e.clientY - last.y;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      // Two fingers pinch to zoom. One finger drags the island about -
      // Matt: "I think I want my thumb swiping around to pan in this one."
      // Distance from where the finger went DOWN, not the sum of every
      // twitch: a real finger held still for half a second still reports
      // a stream of sub-pixel moves, and summing them cancelled every long
      // press at the table (playtest 6, "press and hold... doesn't seem to
      // be the case any more").
      if (this.down) this.down.moved = Math.hypot(e.clientX - this.down.x, e.clientY - this.down.y);
      if (this.pointers.size >= 2) {
        const now = this.spread();
        if (this.pinch > 0 && now > 0) this.world.zoomBy(now / this.pinch);
        this.pinch = now;
        this.moved = true;
        return;
      }
      if (this.down && this.down.moved > DRAG_PX) { this.dragging = true; this.moved = true; this.clearHold(); }
      if (this.dragging) this.world.panBy(dx, dy);
    },
    onUp(e) {
      this.clearHold();
      const wasTap = this.pointers.size === 1 && this.down && this.down.moved <= DRAG_PX && !this.held;
      this.held = false;
      this.pointers.delete(e.pointerId);
      if (this.pointers.size < 2) this.pinch = 0;
      if (wasTap) this.tapAt(e);
      if (!this.pointers.size) { this.dragging = false; this.down = null; }
    },
    onCancel(e) {
      this.clearHold();
      this.pointers.delete(e.pointerId);
      if (!this.pointers.size) { this.dragging = false; this.down = null; this.pinch = 0; }
    },
    onWheel(e) {
      if (this.world) { this.world.zoomBy(e.deltaY < 0 ? 1.12 : 1 / 1.12); this.moved = true; }
    },
    spread() {
      const [a, b] = [...this.pointers.values()];
      return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
    },
    reset() {
      if (this.world) this.world.resetView();
      this.moved = false;
    },
    findBand() {
      if (this.world) this.world.lookAtHex(this.at.q, this.at.r);
      this.moved = true;
    },
    clearHold() {
      if (this.hold) { window.clearTimeout(this.hold); this.hold = null; }
    },
    // The hold went the distance: say what is under the finger, and make
    // sure letting go does not also count as a tap.
    holdFired() {
      this.hold = null;
      if (!this.world || !this.down) return;
      const r = this.$refs.canvas.getBoundingClientRect();
      const nx = ((this.down.x - r.left) / r.width) * 2 - 1;
      const ny = -(((this.down.y - r.top) / r.height) * 2 - 1);
      const hex = this.world.pickHex(nx, ny);
      if (!hex) return;
      this.held = true;
      // Where on the canvas the finger was, so the popover's arrow can point
      // back at the hex it is describing (house style rule 7).
      this.$emit('inspect', { ...hex, atX: this.down.x - r.left, atY: this.down.y - r.top });
    },
    tapAt(e) {
      if (!this.world) return;
      const r = this.$refs.canvas.getBoundingClientRect();
      const nx = ((e.clientX - r.left) / r.width) * 2 - 1;
      const ny = -(((e.clientY - r.top) / r.height) * 2 - 1);
      const hit = this.world.pick(nx, ny);
      if (hit) this.$emit('pick', hit);
    },
  },
};
</script>
